import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useResettablePagination } from "../../hooks/useResettablePagination";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { NavLink } from 'react-router-dom';
import { 
    Box, 
    Typography, 
    Tooltip,
    useTheme,
    Link,
} from "@mui/material";
import { 
  EditOutlined as Edit,
  Done as trueIcon,
  Close as falseIcon,
} from "@mui/icons-material";
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import styles from '../../styles/component.module.scss';
// Utils
import { clearGeneratedTestCase } from "../../features/testCases/testCasesSlice";
import { setActivePage } from "../../features/navigation/navigationSlice";
import { selectProject, setProjectListVisibleColumn } from "../../features/projects/ProjectSlice";
import DEFAULT_ROUTES from "../../constants/routes";
import { useSnackbar } from "../../utils/useSnackbar";
import { MissingData } from "../../enums/enumRegistry";
import { getProjectsList, deleteProject } from "../../utils/api/project_apis";
import { STATUS } from "../../utils/api/sharedfunction";
import QuickSearchInput from "../Query/QuickSearchInput";

// Components
import ConfirmationModal from "../Modal/ConfirmationModal";
import ActionMenu from "../menu/ActionMenu";
import CreateProjectModal from "../Modal/CreateProjectModal";
import EditProjectModal from "../Modal/EditProjectModal";
import TableComponent from '../TableComponent';
import ProjectEmptyScreen from "./ProjectEmptyScreen";
import quickSearchBarStyles from '../../styles/quickSearchBar.module.scss';
import { dateFormat, formatDate , renderDateCell} from '../../utils/date';
import RowActionMenu from '../menu/RowActionMenu';
import { renderTableCell } from "../../utils/commonfunctions";
import { getDeleteModalTitle, getDeleteConfirmMessage } from "../../utils/uiText";
import { resetAllState } from "../../features/replayslice";
import { reset as resetQueryState } from "../../features/query/querySlice";

const getStatusAndText = (context) => {
    const IconComponent = context ? trueIcon : falseIcon;
    return (
      <Box display="flex" alignItems="center">
        <IconComponent fontSize="small" color={context ? "success" : "error"} />
      </Box>
    );
};

const ProjectListTab = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  
  // States
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [projectForAction, setProjectForAction] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editProject, setEditProject] = useState({});
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const isMultiAction = selectedRows.length > 0;
  const columnVisible = useSelector(state => {return state.projects.columnVisible || {}});

  // Sorting state
  const [sortModel, setSortModel] = useState([]);

  // Quick search input value (controlled) and the debounced AST sent to the API
  const [quickSearchValue, setQuickSearchValue] = useState("");
  const [filterAst, setFilterAst] = useState(null);

  // Pagination state — snaps back to page 0 when the filter/search changes.
  const [paginationModel, setPaginationModel] = useResettablePagination({
    pageSize: 25,
    resetKey: JSON.stringify(filterAst),
  });

  useEffect(() => {
    fetchProjects();
  }, [sortModel, paginationModel.page, paginationModel.pageSize, filterAst]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const sortField = sortModel && sortModel.length > 0 ? sortModel[0].field : 'name';
      const sortOrder = sortModel && sortModel.length > 0 ? sortModel[0].sort : 'asc';
      
      // Convert 0-based page index to 1-based for API
      const result = await getProjectsList({
        sortField,
        sortOrder,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        ...(filterAst ? { filterAst } : {})
      });
      
      if (result.status === STATUS.SUCCESS) {
        const data = result.data;
        setProjects(data?.rows || []);
        setTotalRowCount(data?.pagination?.totalItems || data?.pagination?.total || 0);
      } else {
        showSnackbar(result.error || "Failed to fetch projects", "error");
        setProjects([]);
        setTotalRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      showSnackbar(error?.message || "Failed to fetch projects", "error");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const projectToDelete = isMultiAction ? selectedRows : [projectForAction.id];
      
      const result = await deleteProject({ ids: projectToDelete });

      if (result.status === STATUS.SUCCESS) {
        if (selectedProject && projectToDelete.includes(selectedProject.id)) {
          dispatch(selectProject(null));
        }
        
        await fetchProjects();
        
        const message = result.message || "Project deleted successfully";
        showSnackbar(message, "success");
        setSelectedRows([]);
      } else {
        showSnackbar(result.error || "Failed to delete project(s)", "error");
      }
    } catch (error) {
      console.error("Error deleting project(s):", error);
      showSnackbar(error?.message || "Failed to delete project(s)", "error");
    } finally {
      setIsDeleting(false);
      setLoading(false);
    }
  };

  const handleProjectDelete = (project) => {
    setProjectForAction(project);
    setIsDeleting(true);
  };

  const handleProjectEdit = (project) => {
    setEditProject(project);
    setIsEditing(true);
  };

  const handleAdd = () => {
    setIsCreating(true);
  };

  const handleDelete = () => {
    setIsDeleting(true);
  };

  const handleCreateProject = (newProject) => {
    fetchProjects();
    showSnackbar("Project added successfully!", "success");
  };

  const handleEditProject = (updatedProject) => {
    fetchProjects();
    showSnackbar("Project updated successfully!", "success");
  };

  const rowActionButtons = [
    {
      name: "Edit",
      tooltip: "Edit Project",
      icon: <PencilIcon className='icon' />,
      action: (project) => handleProjectEdit(project),
      disabled: isMultiAction,
    },
    {
      name: "Delete",
      tooltip: "Delete Project",
      icon: <TrashIcon className='icon' />,
      action: (project) => handleProjectDelete(project),
      disabled: isMultiAction,
    },
  ];

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const columns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      minWidth: 55,
      maxWidth: 55,
      resizable: false,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 150,
      renderCell: (params) => {
        const handleProjectNavigation = async (event) => {
          if(event) {
            event.preventDefault();
            event.stopPropagation();
          }
          dispatch(clearGeneratedTestCase());
          dispatch(setActivePage("Test Cases"));
          dispatch(selectProject(params.row || {}));
          dispatch(resetAllState());
          dispatch(resetQueryState());
          navigate(`${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${params.row.id}/test-cases`);
        }
        return (
          renderTableCell(params.value, "link", {
            type: 'link',
            to: `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${params.row.id}/test-cases`,
            onClick: handleProjectNavigation
          })
        )
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 3,
      minWidth: 200,
      maxWidth: 350,
      renderCell: (params) => renderTableCell(params.value)
    },
    {
      field:'prefix',
      headerName: 'Key',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => renderTableCell(params.value)
    },
    {
      field: 'context',
      headerName: 'Context',
      flex: 1,
      minWidth: 100,
      maxWidth: 160,
      sortable: false,
      type: 'boolean',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => getStatusAndText(params.value),
    },
    {
      field: 'moduleCount',
      headerName: 'Module Count',
      flex: 1,
      minWidth: 100,
      maxWidth: 150,
      filterable: false,
      sortable: false,
      renderCell: (params) => renderTableCell(params.value),
    },
    {
      field: 'testCaseCount',
      headerName: 'Test Cases Count',
      flex: 1,
      minWidth: 150,
      maxWidth: 150,
      filterable: false,
      sortable: false,
      renderCell: (params) => renderTableCell(params.value)
    },
    {
      field: 'created_at',
      headerName: 'Created On',
      flex: 1,
      minWidth: 130,
      maxWidth: 180,
      type: 'dateTime',
      valueFormatter: (params) => dateFormat(params?.value),
      renderCell: renderDateCell,
    },
    {
      field: 'createdByUser',
      headerName: 'Created By',
      flex: 1.2,
      minWidth: 130,
      maxWidth: 200,
      renderCell: (params) => renderTableCell(params.value?.username)
    },
    {
      field: 'Actions',
      headerName: 'Actions',
      flex: 0,
      minWidth: 90,
      maxWidth: 90,
      hideable: false,
      sortable: false,
      filterable: false,
      resizable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: (params) => (
        <RowActionMenu 
          menuItems={rowActionButtons} 
          rowData={params.row} 
          isDisabled={isMultiAction}
        />
      ),
    },
  ];

  const headerActionButtons = [
    {
      name: "Add",
      tooltip: "Add New Project",
      icon: <PlusIcon className="icon" />,
      action: handleAdd,
      disabled: isMultiAction || loading,
    },
    {
      name: "Delete",
      tooltip: "Delete Selected Project(s)",
      icon: <TrashIcon className="icon" />,
      action: handleDelete,
      disabled: selectedRows.length === 0 || loading,
    }
  ];

  const handleQuickSearchChange = useCallback((ast) => {
    setLoading(true);
    // Send the AST object directly instead of serialising to an ANTLR query string.
    // Keeps user input as a structured JSON value, not a query-language token,
    // which avoids WAF false-positives on patterns like ' OR.
    setFilterAst(ast ?? null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  // Match TestCaseList: no handlePaginationModelChange so grid does not overwrite page on sync
  const handleCustomPaginationPageChange = useCallback((event, page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  }, []);
  const handleCustomPaginationPageSizeChange = useCallback((pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  }, []);

  const getRowClassName = useCallback(
    (params) => (params.row.id === selectedProject?.id ? "selected-row" : ""),
    [selectedProject?.id]
  );

  const customStyles = useMemo(
    () => ({
      "& .selected-row": {
        backgroundColor: theme.palette.customColor.blue[50],
        "&:hover": {
          backgroundColor: theme.palette.customColor.blue[100],
        },
      },
    }),
    [theme.palette.customColor?.blue?.[50], theme.palette.customColor?.blue?.[100]]
  );

  const headerContentRightElement = useMemo(
    () => (
      <Box className={quickSearchBarStyles.quickSearchBarWrap}>
        <QuickSearchInput
          entityType="project"
          value={quickSearchValue}
          onChange={setQuickSearchValue}
          onSearchChange={handleQuickSearchChange}
          placeholder="Search projects..."
        />
      </Box>
    ),
    [quickSearchValue, handleQuickSearchChange]
  );

  const headerContentRight = useCallback(() => headerContentRightElement, [headerContentRightElement]);
  const isSearchOrFilterActive = quickSearchValue.trim().length > 0 || Boolean(filterAst);
  const hasNoProjects = !isSearchOrFilterActive && totalRowCount === 0;
  const showCustomNoData = !loading && hasNoProjects;

  return (
    <>
      <TableComponent
        title="Project List"
        headerActionButtons={headerActionButtons}
        headerContentRight={headerContentRight}
        rows={projects}
        columns={columns}
        loading={loading}
        idField="id"
        isCheckbox={true}
        isRowSelection={true}
        handleRowSelection={(selectionModel) => setSelectedRows(selectionModel)}
        selectedRows={selectedRows}
        paginationModel={paginationModel}
        handleCustomPaginationPageChange={handleCustomPaginationPageChange}
        handleCustomPaginationPageSizeChange={handleCustomPaginationPageSizeChange}
        isPagination={true}
        paginationMode="server"
        isCustomPagination={true}
        rowCount={totalRowCount}
        isColumnResizable={!loading}
        viewType="projectList"
        getRowHeight={() => 'auto'}
        //tableHeight={'calc(100vh - 330px)'}
        sortModel={sortModel}
        handleSortModelChange={handleSortModelChange}
        sortingMode="server"
        getRowClassName={getRowClassName}
        hideColumns={['name']}
        customStyles={customStyles}
        isData={true}
        isVisibleInsideTableNoData={showCustomNoData}
        customNoData={<ProjectEmptyScreen onCreate={() => setIsCreating(true)} />}
      />
      
      {/* Popup Modals */}
      <>
        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          open={isDeleting}
          onClose={() => setIsDeleting(false)}
          onConfirm={confirmDelete}
          title={getDeleteModalTitle()}
          message={getDeleteConfirmMessage('project', selectedRows.length, isMultiAction)}
        />

        {/* Create Project Modal */}
        <CreateProjectModal
          open={isCreating}
          onClose={() => setIsCreating(false)}
          onCreate={handleCreateProject}
          onError={(errorType, errorMessage) => {
            if (errorType === 'error validate') return;
            if (errorType === 'error duplicate') {
                showSnackbar("A project with this name already exists!", "error");
            } else {
                showSnackbar(errorMessage || "Error creating project", "error");
            }
        }}
          projects={projects}
          showSnackbar={showSnackbar}
        />

        {/* Edit Project Modal */}
        <EditProjectModal
          open={isEditing}
          onClose={() => setIsEditing(false)}
          onEdit={handleEditProject}
          onError={(errorType, errorMessage) =>
            showSnackbar(errorMessage || "Error updating project", "error")
          }
          projectToUpdate={editProject}
          projects={projects}
        />
      </>
    </>
  );
};

export default ProjectListTab;